import IntroComponent from '../components/intro.components';
import InvestIcon from '../../../assets/invest-icon.svg';
import ArrowUpLightGreen from '../../../assets/arrow-up-light-green-icon.svg';
import InformIcon from '../../../assets/inform-dark-green-icon.svg';
import LeafIcon from '../../../assets/leaf-icon.svg';
import SeedIcon from '../../../assets/seed-icon.svg';
import RightArrow from '../../../assets/arrow-right-black.svg';
import { getInvestmentPointHistory } from '../../../apis/article.api';
import { useQuery } from '@tanstack/react-query';
import { formartNumber } from '../../../utils/helper.util';
import { InvestmentsTableComponent } from '../components/investments-table.component';
import { TooltipComponent } from '../../Tooltip';
import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { profileAtom } from '../../Settings/atom';

const InvestmentsComponent = () => {
  const [dataTable, setDataTable] = useState([]);
  const [pagination, setPagination] = useState({
    limit: 4,
    page: 1,
    sortBy: undefined,
    sort: undefined,
    search: undefined
  });
  const [profile] = useAtom(profileAtom);

  const params = {
    ...pagination
  };

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );

  const getStylistTooltipSeedToInvest = () => {
    return (
      <div className="m-2">
        <div className="flex justify-center">
          <div className="flex flex-row gap-1">
            <img src={LeafIcon} alt="" />
            <b>Received Twigs</b>
            <img src={RightArrow} alt="" />
            <img src={SeedIcon} alt="" />
            <b>Seeds</b>
          </div>
        </div>
        <p>
          The amount of Seeds available for you to invest is the total number of Twigs you've been
          awarded by others, minus the number of Seeds you've already invested in articles.
        </p>
        <b>Twigs received - Seeds invested = Available Seeds for investing</b>
      </div>
    );
  };

  const {
    data: investmentPointHistory,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['getInvestmentPointHistory', filteredParams],
    queryFn: () => getInvestmentPointHistory(filteredParams)
  });

  useEffect(() => {
    if (investmentPointHistory) {
      setDataTable(investmentPointHistory);
    }
  }, [investmentPointHistory]);

  return (
    <div className="flex flex-col gap-6">
      <IntroComponent
        title="My Investments"
        des="View earnings you have made from investing in articles"
      />

      <div className="px-4 md:px-0 flex items-center gap-5 flex-col md:flex-row">
        <div className="bg-white p-6 rounded-[12px] border border-05-seasalt-white shadow-[0px_4px_4px_0px_#F2F3F780] w-full md:w-fit">
          <div className="flex items-center">
            <img src={InvestIcon} className="w-5 h-5" alt="" />
            <p className="font-[800] ml-[5px] text-sm">Investments Breakdown</p>
          </div>
          <div className="mt-6 flex flex-wrap md:flex-nowrap gap-4">
            <div className="flex flex-col gap-2 w-full md:w-auto pr-4 border-b md:border-b-0 border-r-0 md:border-r border-[#E6E7EB]">
              <div className="flex gap-1">
                <p className="text-05-growth-green-900 font-[800] text-sm">
                  Available Seeds to invest
                </p>
                <TooltipComponent
                  maxW="420px"
                  placement="top"
                  Icon={InformIcon}
                  className="w-5 h-5"
                  description={getStylistTooltipSeedToInvest()}
                />
              </div>
              <p className="text-2xl text-growth-green-900 font-[800] flex items-center gap-1">
                {profile?.totalRawPointsRemaining || 0}
                <span className="text-sm font-[400]">Seeds</span>
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto pr-4 border-b md:border-b-0 border-r-0 md:border-r border-[#E6E7EB]">
              <p className="text-05-growth-green-900 font-[800] text-sm">Total Seeds Invested</p>
              <p className="text-2xl text-growth-green-900 font-[800] flex items-center gap-1">
                {dataTable?.data?.totalUserPointsInvested || 0}
                <span className="text-sm font-[400]">Seeds</span>
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto pr-4 border-b md:border-b-0 border-r-0 md:border-r border-[#E6E7EB]">
              <div className="flex gap-1">
                <p className="text-05-growth-green-900 font-[800] text-sm">Active Articles</p>{' '}
                <TooltipComponent
                  maxW="337px"
                  placement="top"
                  Icon={InformIcon}
                  className="w-5 h-5"
                  description="Number of articles you have invested in"
                />
              </div>

              <p className="text-2xl text-growth-green-900 font-[800] flex items-center gap-1">
                {dataTable?.data?.activeArticles || 0}{' '}
                <span className="text-sm font-[400]">Investments</span>
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-05-growth-green-900 text-sm font-[800]">Total Earnings</p>
              <div className="flex items-center gap-4">
                <p className="text-growth-green-900 text-2xl font-[800]">
                  ${formartNumber(dataTable?.data?.totalEarned, 2) || 0}
                </p>
                <a
                  className="flex gap-2 items-center hover:opacity-80 cursor-pointer"
                  href="/creator-dashboard/earnings/my-earnings"
                >
                  <p className="text-growth-green-500 text-sm font-[900]">For more details</p>
                  <img className="fill-growth-green-500" src={ArrowUpLightGreen} alt="" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <InvestmentsTableComponent
        dataTable={dataTable?.data?.articleInvestments}
        totalPage={dataTable.totalPage}
        pagination={pagination}
        setPagination={setPagination}
        refetch={refetch}
        isFetching={isFetching}
      />
    </div>
  );
};

export default InvestmentsComponent;
